import dynamic from 'next/dynamic';
import { useState, useEffect } from 'react';
import { useMount } from 'ahooks';
import css from './index.module.scss';
import PageLayout from '@/components/PageLayout';
import { isReadyLang, redirectTo404, efFetch, getHostApi, initH1Tage } from '@/utils';
import { useGlobal } from '@/utils/useGlobal';
import SeriesTypePc from '@/components/SeriesType';
import SeriesTypeH5 from '@/componentsH5/SeriesType';
import NaverPc from '@/components/NaverT';
import { getPageList, getCategoryList, cacheOrCatch } from '@/service';
import SEOlinksA from '@/components/SEOlinksA';
import seoconfig from '@/seoconfig/collections/series';
import { EU_SITE } from '@/constants';
import { newHeaderLocales } from '@/components/PageLayout/newHeaderLocales';

// v2: 官网 2.0 首页具备上线条件的国家
const readyV2Lang = ['es'];

const SeriesTypePcV2 = dynamic(() => import('@/components/SeriesTypeV2'), {
    ssr: true,
});
const SeriesTypeH5V2 = dynamic(() => import('@/componentsH5/SeriesTypeV2'), {
    ssr: false,
});
const NaverPcV2 = dynamic(() => import('@/components/NaverT'), {
    ssr: true,
});
const NaverH5V2 = dynamic(() => import('@/componentsH5/NaverT'), {
    ssr: false,
});

const sourceIdMap: any = {
    river: '1468461339749761025',
    delta: '1468461504103563266',
    'solar-panels': '1468461916013576194',
    solar: '1468461916013576194',
    smart: '1468462465052164098',
};

const h1Tag: any = {
    delta: 'DELTA Pro',
    river: 'RIVER Pro',
    solar: '400W Portable Solar Panel',
    smart: 'DELTA Pro',
};

export default function Collections({ SEO, categories, data, lang }: any) {
    const { isMobile, useRouter, gtm } = useGlobal();
    const router = useRouter();
    const { locale } = router;
    const hideHeaderNav = newHeaderLocales.includes(locale);

    // v2
    const isV2Flag = true; //readyV2Lang.includes(locale);
    const SeriesType = isMobile ? (isV2Flag ? SeriesTypeH5V2 : SeriesTypeH5) : isV2Flag ? SeriesTypePcV2 : SeriesTypePc;
    const Naver = isMobile ? (isV2Flag ? NaverH5V2 : NaverPc) : isV2Flag ? NaverPcV2 : NaverPc;

    const typeList: any = {
        'portable-power-stations': 'delta',
        power: 'delta',
        product_ps_delta: 'delta',
        product_ps_river: 'river',
        product_sp: isV2Flag ? 'panels' : 'solar',
        product_smart: isV2Flag ? 'devices' : 'smart',
        'solar-panels': isV2Flag ? 'panels' : 'solar',
        solar: isV2Flag ? 'panels' : 'solar',
        'smart-devices': isV2Flag ? 'devices' : 'smart',
        'smart-home-ecosystem': isV2Flag ? 'devices' : 'smart',
        smart: isV2Flag ? 'devices' : 'smart',
    };
    const {
        query: { series },
        asPath,
    } = router;

    const selectDataType = typeList[series || 'power'];
    const [active, setActive] = useState(selectDataType);
    const content = isV2Flag ? data : isMobile ? data?.h5 : data?.pc; //共有 river,delta,solar,smart
    const [contentData, setContentData] = useState(content ? (isV2Flag ? content[selectDataType]?.pageLayoutList : content[selectDataType]) : []);
    const [currentSEO, setCurrentSEO] = useState([]);
    const [seoMap, setSeoMap] = useState<any>([]);

    const tabActive = (e: any) => {
        const keystr = typeList[e];
        setContentData(isV2Flag ? content[keystr]?.pageLayoutList : content[keystr]);
        setActive(keystr);
        setCurrentSEO(seoMap.find((item: any) => item.sourceId === sourceIdMap[keystr]));
    };

    useEffect(() => {
        // https://ecoflow.feishu.cn/docx/L2MUdAlXhoxbScxXPfmcaZ1dnRc
        if (EU_SITE.includes(locale)) {
            if (series === 'power') {
                router.push(`/${locale}/collections/portable-power-stations`);
            } else if (series === 'solar') {
                router.push(`/${locale}/collections/solar-panels`);
            } else if (series === 'smart') {
                router.push(`/${locale}/collections/smart-devices`);
            }
            return;
        }
        if (!isV2Flag) {
            setActive(typeList[series]);
            setContentData(content[typeList[series]]);
        }
        initH1Tage(h1Tag[typeList[series || 'power']]);
    }, [series]);

    useEffect(() => {
        // 仅用于Emarsys埋点
        const categoryNameMap: { [key: string]: string } = {
            delta: 'Portable Power Stations > DELTA Series',
            river: 'Portable Power Stations > RIVER Series',
            panels: 'Solar Panels > Portable',
            solar: 'Solar Panels > Portable',
            devices: 'Smart Devices',
            smart: 'Smart Devices',
        };
        if (categoryNameMap[active]) gtm.EmPush(['category', categoryNameMap[active]]);
    }, [active]);

    useMount(() => {
        const fetchData = async () => {
            try {
                const API_HOST = getHostApi(locale).NEXT_PUBLIC_API_HOST; //process.env.NEXT_PUBLIC_API_HOST
                const fetchRes = await cacheOrCatch({
                    requestQuery: efFetch(`${API_HOST}/series/data?locale=${locale}`),
                    cachePath: `product/data/${locale}.json`,
                });
                const smap = fetchRes?.data?.seoPageResponseList || [];
                setSeoMap(smap);
                setCurrentSEO(smap.find((item: any) => item.sourceId === sourceIdMap[selectDataType]));
            } catch (e) {
                console.error(e);
            }
        };

        fetchData();
    });

    if (typeof window !== 'undefined') {
        if (locale === 'en') {
            // 多语言按需加载，URL 中如有语言切换需重新初始化整个页面，不使用 router 路由
            window.location.href = `/us${asPath}`;
            return null;
        }
    }

    return (
        <>
            <SEO title={currentSEO?.title} description={currentSEO?.description} />
            <SEOlinksA locale={locale} list={seoconfig[series]?.include} pathname={seoconfig[series]?.url} />
            <PageLayout theme="gray">
                {!isV2Flag && (
                    <div className={`${css.collections} series-out-item series-out-item-${active}`}>
                        {!hideHeaderNav && <Naver query={series} categoryList={categories} callback={tabActive} />}
                        <SeriesType serieType={active} contentData={contentData} lang={lang} />
                    </div>
                )}

                {isV2Flag && (
                    <>
                        {!hideHeaderNav && <Naver query={series} categoryList={categories} callback={tabActive} />}
                        <SeriesType serieType={active} contentData={contentData} lang={lang} />
                    </>
                )}
            </PageLayout>
        </>
    );
}

export async function getServerSideProps({ locale }: any) {
    if (locale !== 'en') {
        if (!isReadyLang(locale)) {
            return redirectTo404();
        }
    }

    // v2
    const isV2Flag = true; //readyV2Lang.includes(locale);

    // 非v2版本
    let categories: any = [];
    let collectionData: any = null;

    try {
        const resCategory = await getCategoryList(locale);
        if (resCategory.code === '0') {
            categories = resCategory?.data;
        }
    } catch (err) {
        console.log(err);
    }
    try {
        collectionData = require(`@/data/collection/${locale}`);
    } catch (e) {
        collectionData = require(`@/data/collection/en`);
    }

    // v2
    const pageMap = {} as any;
    if (isV2Flag) {
        try {
            const res = await getPageList(locale);
            if (res.code === '0') {
                res.data.forEach((element: { pageLayoutList: any; pageKey: string | number }) => {
                    pageMap[element.pageKey] = element;
                });
            }
        } catch (e) {
            console.log('e', e);
        }
    }

    return {
        props: {
            data: isV2Flag
                ? pageMap
                : {
                      pc: collectionData(false),
                      h5: collectionData(true),
                  },
            categories,
        },
    };
}
